import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

interface Orb {
  x: number;
  y: number;
  size: number;
  color: string;
  duration: number;
  delay: number;
}

/** Seeded random so the decoration is stable between renders */
function seededRandom(seed: number): () => number {
  let state = seed;
  return () => {
    state = (state * 9301 + 49297) % 233280;
    return state / 233280;
  };
}

/**
 * Diwali sale banner (shared). Fully responsive: scales type and spacing with the viewport
 * and stacks the offer pills / hides decorations on small screens.
 * Usage: <ipm-diwali-sale-banner></ipm-diwali-sale-banner>
 */
@Component({
  selector: 'ipm-diwali-sale-banner',
  templateUrl: './diwali-sale-banner.component.html',
  styleUrls: ['./diwali-sale-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiwaliSaleBannerComponent {
  @Input() eyebrow: string = 'Festival of Lights · Festival of Beats';
  @Input() salePeriod: string = '31st October – 7th November';
  @Input() beatOffer: string = '50% OFF';
  @Input() planOffer: string = '45% OFF';
  @Input() ctaLabel: string = 'Enroll Now';
  @Input() ctaLink: string = '/enroldiwalisale';
  /** Producer already enrolled their beats: the button reads "Enrolled" instead of the cta label */
  @Input() enrolled: boolean = false;
  /** false when the user is already on Pro Annual: the Pro Annual offer pill is hidden */
  @Input() showPlanOffer: boolean = true;
  /** The Pro Annual offer pill is a button (the sale is live, so the Diwali Pro popup can open); plain text before that */
  @Input() planOfferClickable: boolean = false;
  /** Pro Annual offer pill clicked: the parent opens the Diwali Pro popup */
  @Output() planOfferClick = new EventEmitter<void>();
  /** Sale dates for the countdown under the button: "Sale starts in" before startAt, "Sale ends in" until endAt */
  @Input() startAt: string | null = null;
  @Input() endAt: string | null = null;

  readonly orbs: Orb[] = this.buildOrbs();
  readonly diyaPositions: string[] = ['8%', '17%', '83%', '92%'];

  trackByIndex(index: number): number {
    return index;
  }

  onPlanOfferClick(): void {
    if (this.planOfferClickable) {
      this.planOfferClick.emit();
    }
  }

  private buildOrbs(): Orb[] {
    const random = seededRandom(7);
    return Array.from({ length: 36 }, () => {
      const hue = random();
      return {
        x: random() * 100,
        y: random() * 100,
        size: 4 + random() * 26,
        color: hue < 0.6 ? '255,170,60' : hue < 0.85 ? '255,110,30' : '160,90,255',
        duration: 3 + random() * 5,
        delay: random() * -6,
      };
    });
  }
}
